import {
  Body1Strong,
  InteractionTag,
  InteractionTagPrimary,
  Subtitle1,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import { useApplyPrompt, useAuth } from '../../hooks'
import type { Prompt } from '../../types/prompt'
import { resolvePromptLanguage } from '../../types/prompt'

// Non-localized fields for the starter suggestion prompts; title/description/userPrompt/systemPrompt come from home.suggestions.* translations.
const SUGGESTION_PROMPT_META = [
  {
    promptId: 'suggestion-summarize',
    translationKey: 'summarize',
    llmModel: 'claude-4.7-opus',
    promptType: 'standard',
    isFavorite: false,
    reviewMode: null,
    requiresDocumentComments: false,
    requiresFullDocument: true,
  },
  {
    promptId: 'suggestion-draft-section',
    translationKey: 'draftSection',
    llmModel: 'claude-4.7-opus',
    promptType: 'standard',
    isFavorite: false,
    reviewMode: null,
    requiresDocumentComments: false,
    requiresFullDocument: false,
  },
] as const

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingHorizontalL,
    height: '100%',
  },
  inner: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    alignItems: 'flex-start',
    width: '100%',
  },
  heading: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  titleLine: {
    margin: 0,
  },
  tags: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    alignItems: 'flex-start',
  },
})

export const EmptyHomeState = () => {
  const { t, i18n } = useTranslation()
  const styles = useStyles()
  const { displayName } = useAuth()
  const { applyPrompt } = useApplyPrompt({ fetchDocumentName: true })

  // Match backend prompts: only localize into supported prompt languages, even if ja.json gains these keys.
  const lng = resolvePromptLanguage(i18n.resolvedLanguage)
  const suggestionPrompts: Prompt[] = SUGGESTION_PROMPT_META.map(({ translationKey, ...meta }) => ({
    ...meta,
    title: t(`home.suggestions.${translationKey}.title`, { lng }),
    description: t(`home.suggestions.${translationKey}.description`, { lng }),
    userPrompt: t(`home.suggestions.${translationKey}.userPrompt`, { lng }),
    systemPrompt: t(`home.suggestions.${translationKey}.systemPrompt`, { lng }),
  }))

  const handlePromptClick = (prompt: Prompt) => {
    applyPrompt(prompt)
  }

  return (
    <div className={styles.root}>
      <div className={styles.inner}>
        <div className={styles.heading}>
          <Body1Strong as="p">{t('home.greeting', { name: displayName })}</Body1Strong>
          <Subtitle1 as="h2" className={styles.titleLine}>
            {t('home.getStarted')}
          </Subtitle1>
        </div>
        <div className={styles.tags}>
          {suggestionPrompts.map((prompt) => (
            <InteractionTag
              key={prompt.promptId}
              size="medium"
              appearance="brand"
              shape="circular"
              onClick={() => handlePromptClick(prompt)}>
              <InteractionTagPrimary>{prompt.title}</InteractionTagPrimary>
            </InteractionTag>
          ))}
        </div>
      </div>
    </div>
  )
}
